import type { ApiErrorPayload, HttpMethod } from "@/types";

const EDGE_ENDPOINT_PREFIXES = ["/api/polymarket/", "/api/explorer/"] as const;

const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
const config = window.SOVEREIGN_CONFIG ?? {};
const viteEnv = import.meta.env;

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname === "0.0.0.0" ||
  window.location.hostname === "::1";

const envApiBase = (viteEnv.VITE_API_URL ?? "").trim();
const envEdgeApiBase = (viteEnv.VITE_EDGE_API_URL ?? "").trim();
const envWsBase = (viteEnv.VITE_WS_URL ?? "").trim();

const configuredApiBase = config.API_URL ?? envApiBase;
const originApiBase = (configuredApiBase || window.location.origin).replace(/\/$/, "");

const configuredEdgeApiBase = config.EDGE_API_URL ?? envEdgeApiBase;
const edgeApiBase = (configuredEdgeApiBase || originApiBase).replace(/\/$/, "");

const configuredWsBase = config.WS_URL ?? envWsBase;
export function normalizeWsBaseUrl(rawBase: string): string {
  const base = rawBase.replace(/\/$/, "");
  if (!base) {
    return base;
  }
  try {
    const parsed = new URL(base);
    if (
      parsed.hostname === "trading.neuralnetninja.com" &&
      parsed.pathname.replace(/\/$/, "") === ""
    ) {
      parsed.pathname = "/api";
      return parsed.toString().replace(/\/$/, "");
    }
  } catch {
    return base;
  }
  return base;
}

const wsBase = normalizeWsBaseUrl(
  configuredWsBase ||
    (isLocalhost
      ? `${protocol}//${window.location.host}`
      : `${protocol}//${window.location.host}/api`),
);

export class ApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload | string | null;

  constructor(message: string, status: number, payload: ApiErrorPayload | string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export function shouldUseEdgeEndpoint(endpoint: string): boolean {
  return EDGE_ENDPOINT_PREFIXES.some((prefix) => endpoint.startsWith(prefix));
}

type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();

/**
 * Register a callback fired when a same-origin /api/* request returns 401
 * (session expired). Returns an unregister function. /api/auth/* endpoints are
 * excluded (login itself 401s on bad credentials), as are edge-prefixed
 * endpoints whose 401 does not mean the dashboard session died.
 */
export function registerUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => {
    unauthorizedHandlers.delete(handler);
  };
}

function shouldNotifyUnauthorized(endpoint: string): boolean {
  return (
    endpoint.startsWith("/api/") &&
    !endpoint.startsWith("/api/auth/") &&
    !shouldUseEdgeEndpoint(endpoint)
  );
}

function notifyUnauthorized(): void {
  for (const handler of unauthorizedHandlers) {
    handler();
  }
}

export function buildApiUrl(endpoint: string): string {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  const base = shouldUseEdgeEndpoint(endpoint) ? edgeApiBase : originApiBase;
  if (!base) {
    return endpoint;
  }
  if (endpoint.startsWith("/")) {
    return `${base}${endpoint}`;
  }
  return `${base}/${endpoint}`;
}

// Error/diagnostic payload snippets are truncated so an HTML error page never
// floods the UI (or error-boundary/toast consumers) with raw markup.
const MAX_PAYLOAD_SNIPPET_LENGTH = 200;

function looksLikeHtml(payload: string): boolean {
  return payload.trimStart().startsWith("<") || /<html/i.test(payload);
}

function normalizeErrorMessage(
  status: number,
  payload: ApiErrorPayload | string | null,
): string {
  if (typeof payload === "string" && payload.length > 0) {
    if (looksLikeHtml(payload)) {
      return `Received an HTML error page (status ${status}) — re-authentication may be required (edge/auth session expired). Reload the page.`;
    }
    return payload.slice(0, MAX_PAYLOAD_SNIPPET_LENGTH);
  }
  if (payload && typeof payload === "object") {
    if (typeof payload.detail === "string") {
      return payload.detail;
    }
    if (typeof payload.message === "string") {
      return payload.message;
    }
  }
  return `Request failed with status ${status}`;
}

async function parseErrorPayload(
  response: Response,
): Promise<ApiErrorPayload | string | null> {
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    return null;
  }
  if (raw.length === 0) {
    return null;
  }
  if ((response.headers.get("content-type") ?? "").includes("application/json")) {
    try {
      return JSON.parse(raw) as ApiErrorPayload;
    } catch {
      return raw.slice(0, MAX_PAYLOAD_SNIPPET_LENGTH);
    }
  }
  return raw.slice(0, MAX_PAYLOAD_SNIPPET_LENGTH);
}

async function parseSuccessPayload<TResponse>(response: Response): Promise<TResponse> {
  // 204/empty-body carve-out: No-Content responses have nothing to parse.
  if (response.status === 204) {
    return undefined as TResponse;
  }
  const raw = await response.text();
  if (raw.length === 0) {
    return undefined as TResponse;
  }
  if ((response.headers.get("content-type") ?? "").includes("application/json")) {
    try {
      return JSON.parse(raw) as TResponse;
    } catch {
      throw new ApiError(
        `Invalid JSON from API (status ${response.status})`,
        response.status,
        raw.slice(0, MAX_PAYLOAD_SNIPPET_LENGTH),
      );
    }
  }
  // A non-JSON 2xx is not data — it is typically a Cloudflare Access / edge
  // interstitial page. Surfacing it as typed data would render fake-calm
  // zeros in money panels; fail loudly instead.
  throw new ApiError(
    `Non-JSON response from API (status ${response.status}) — re-authentication may be required (auth gateway or edge session expired)`,
    response.status,
    raw.slice(0, MAX_PAYLOAD_SNIPPET_LENGTH),
  );
}

const DEFAULT_TIMEOUT_MS = 30_000;
const RETRY_STATUSES = new Set([502, 503, 504]);
const RETRYABLE_METHODS = new Set<HttpMethod>(["GET"]);
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 2_000;

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function apiRequest<TResponse>(
  endpoint: string,
  method: HttpMethod,
  body?: unknown,
): Promise<TResponse> {
  const normalizedMethod = method.toUpperCase() as HttpMethod;
  const url = buildApiUrl(endpoint);
  const init: RequestInit = {
    method: normalizedMethod,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  };
  const canRetry = RETRYABLE_METHODS.has(normalizedMethod);

  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    let response: Response;
    try {
      response = await fetchWithTimeout(url, init, DEFAULT_TIMEOUT_MS);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        lastError = new ApiError(`Request timed out after ${DEFAULT_TIMEOUT_MS / 1000}s`, 0, null);
      } else {
        const message =
          error instanceof Error && error.message.length > 0
            ? error.message
            : "Network request failed";
        lastError = new ApiError(`Network error: ${message}`, 0, null);
      }
      if (canRetry && attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }
      throw lastError;
    }

    if (canRetry && RETRY_STATUSES.has(response.status) && attempt < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      continue;
    }

    if (response.status === 401 && shouldNotifyUnauthorized(endpoint)) {
      notifyUnauthorized();
    }

    if (!response.ok) {
      const payload = await parseErrorPayload(response);
      throw new ApiError(normalizeErrorMessage(response.status, payload), response.status, payload);
    }

    return parseSuccessPayload<TResponse>(response);
  }

  throw lastError ?? new ApiError("Request failed after retries", 0, null);
}

export function apiGet<TResponse>(endpoint: string): Promise<TResponse> {
  return apiRequest<TResponse>(endpoint, "GET");
}

export function apiPost<TResponse>(
  endpoint: string,
  body?: Record<string, unknown>,
): Promise<TResponse> {
  return apiRequest<TResponse>(endpoint, "POST", body);
}

export function apiDelete<TResponse>(endpoint: string): Promise<TResponse> {
  return apiRequest<TResponse>(endpoint, "DELETE");
}

export function getWsBaseUrl(): string {
  return wsBase;
}
